import { useState, useCallback } from 'react';
import type { KbCategory } from '@/mocks/adminKnowledgeBase';
import { allCategories, categoryMeta } from '@/mocks/adminKnowledgeBase';

interface KnowledgeBaseFiltersProps {
  onSearch: (query: string) => void;
  onCategoryChange: (category: KbCategory | 'todas') => void;
  activeCategory: KbCategory | 'todas';
  totals: Record<KbCategory | 'todas', number>;
}

export default function KnowledgeBaseFilters({
  onSearch,
  onCategoryChange,
  activeCategory,
  totals,
}: KnowledgeBaseFiltersProps) {
  const [searchValue, setSearchValue] = useState('');

  const handleSearch = useCallback(
    (value: string) => {
      setSearchValue(value);
      onSearch(value);
    },
    [onSearch],
  );

  const activeCount = activeCategory === 'todas' ? totals.todas : totals[activeCategory];

  return (
    <div className="space-y-3">
      <div className="relative">
        <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
        <input
          type="text"
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Buscar en la base de conocimiento..."
          className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-foreground-800 text-sm placeholder:text-foreground-400 outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
        />
        {searchValue && (
          <button
            onClick={() => handleSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors"
          >
            <i className="ri-close-line text-foreground-500 text-xs"></i>
          </button>
        )}
      </div>

      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
        <button
          onClick={() => onCategoryChange('todas')}
          className={`shrink-0 px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
            activeCategory === 'todas'
              ? 'bg-foreground-950 text-background-50'
              : 'bg-background-50 border border-background-200 text-foreground-500 hover:bg-background-100'
          }`}
        >
          Todas ({totals.todas})
        </button>
        {allCategories.map((cat) => {
          const meta = categoryMeta[cat];
          const count = totals[cat];
          return (
            <button
              key={cat}
              onClick={() => onCategoryChange(cat)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                activeCategory === cat
                  ? 'bg-foreground-950 text-background-50'
                  : 'bg-background-50 border border-background-200 text-foreground-500 hover:bg-background-100'
              }`}
            >
              <i className={`${meta.icon} text-[10px]`}></i>
              {meta.label} ({count})
            </button>
          );
        })}
      </div>

      {searchValue && (
        <div className="flex items-center gap-2 text-foreground-400 text-[10px]">
          <i className="ri-information-line text-[10px]"></i>
          <span>{activeCount} resultado{activeCount !== 1 ? 's' : ''} para "{searchValue}"</span>
        </div>
      )}
    </div>
  );
}